import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TopBar } from "@/components/dashboard/TopBar";
import { KpiRow } from "@/components/dashboard/KpiRow";
import { TasksPanel } from "@/components/tasks/TasksPanel";
import { AllBikesTable } from "@/components/dashboard/AllBikesTable";
import { AiStrip } from "@/components/dashboard/AiStrip";
import { FocusBoard } from "@/components/dashboard/FocusBoard";
import { ChannelMonth } from "@/components/dashboard/ChannelMonth";
import { ModelBreakdown } from "@/components/dashboard/ModelBreakdown";
import { PartySales } from "@/components/dashboard/PartySales";
import { InventoryFifo } from "@/components/dashboard/InventoryFifo";
import { SubdealerInventory } from "@/components/dashboard/SubdealerInventory";
import { PurchaseAnalysis } from "@/components/dashboard/PurchaseAnalysis";
import { SalesAnalysis } from "@/components/dashboard/SalesAnalysis";
import SellScreen from "@/components/dashboard/SellScreen";
import { RtoTracker } from "@/components/dashboard/RtoTracker";
import { FilterBar, yearDefault } from "@/components/dashboard/FilterBar";
import { TargetsPerformance } from "@/components/dashboard/TargetsPerformance";
import { Settings } from "@/components/dashboard/Settings";
import { AiChat } from "@/components/dashboard/AiChat";
import { LayoutDashboard, Package, FileBadge, Target, ShoppingCart, TrendingUp, FileText } from "lucide-react";

const NAV_ITEMS = [
  ["overview", "Overview", LayoutDashboard],
  ["inventory", "Inventory", Package],
  ["sales", "Sales", TrendingUp],
  ["purchases", "Purchase", ShoppingCart],
  ["sell", "Sell & Estimate", FileText],
  ["rto", "RTO Tracker", FileBadge],
  ["targets", "Targets", Target],
];

export default function Dashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const allowed = user?.role === "admin" ? null : (user?.screens || ["sell", "targets"]);
  const navItems = allowed ? NAV_ITEMS.filter(([v]) => allowed.includes(v)) : NAV_ITEMS;
  useEffect(() => {
    if (allowed && !allowed.includes(tab) && tab !== "settings") setTab(allowed[0] || "sell");
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState(yearDefault);
  const fq = useMemo(() => {
    if (!filters) return "";
    const active = filters.from || filters.to || filters.parties?.length || filters.models?.length || filters.codes?.length || filters.chassis?.length || (filters.rto && filters.rto !== "all");
    return active ? encodeURIComponent(JSON.stringify(filters)) : "";
  }, [filters]);

  const load = useCallback(async () => {
    const { data: d } = await api.get(`/dashboard/data${fq ? `?filters=${fq}` : ""}`);
    setData(d);
    return d;
  }, [fq]);

  useEffect(() => { load().catch(() => toast.error("Failed to load dashboard data")); }, [load]);

  useEffect(() => {
    const h = () => setTab("sell");
    window.addEventListener("prefill-estimate", h);
    return () => window.removeEventListener("prefill-estimate", h);
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/sheet/refresh");
      await load();
      if (r.source === "google_sheet") toast.success(`Synced from Google Sheet · ${r.showroom_rows} showroom rows, ${r.rto_rows} RTO rows`);
      else toast.warning("Sheet still private — showing sample data. Set sharing to 'Anyone with link → Viewer'.");
    } catch (e) {
      toast.error("Refresh failed");
    } finally { setRefreshing(false); }
  };

  const isSettings = tab === "settings";

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <TopBar data={data} refreshing={refreshing} onRefresh={refresh} tab={tab} setTab={setTab} navItems={navItems} />
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-5 space-y-3 sm:space-y-4">
        {data?.source === "sample" && (
          <div data-testid="sample-data-banner" className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
            Showing <b>sample data</b> — your Google Sheet is private. Open the sheet → Share → set <b>"Anyone with the link → Viewer"</b>, then hit Refresh.
          </div>
        )}
        {!isSettings && <FilterBar onApply={setFilters} />}
        {tab === "overview" && (
          <div className="space-y-3 sm:space-y-4">
            <KpiRow kpis={data?.kpis} />
            <AiStrip />
            <FocusBoard />
            <AllBikesTable />
          </div>
        )}
        {tab === "inventory" && (
          <div className="space-y-4">
            <InventoryFifo aging={data?.aging} fifo={data?.fifo} />
            <SubdealerInventory fq={fq} />
            <ModelBreakdown breakdown={data?.model_breakdown} />
          </div>
        )}
        {tab === "sales" && (
          <div className="space-y-4">
            <SalesAnalysis fq={fq} />
            <ChannelMonth />
            <PartySales partySales={data?.party_sales} />
          </div>
        )}
        {tab === "purchases" && <PurchaseAnalysis fq={fq} />}
        {tab === "sell" && <SellScreen isAdmin={user?.role === "admin"} />}
        {tab === "rto" && <RtoTracker rto={data?.rto} />}
        {tab === "targets" && <TargetsPerformance isAdmin={false} />}
        {isSettings && user?.role === "admin" && <Settings />}
      </main>
      <AiChat />
      {(user?.role === "admin" || user?.screens?.includes("tasks")) && <TasksPanel />}
    </div>
  );
}
